/* eslint-disable @applitools/compat/no-async-await */

import { boot } from '@dazn/bootstrap-lib';

import { environments } from '../configuration/constants';

export interface CreateBrandModuleProps {
    currentEnvironment: boot.config.Environment;
    getDeviceId: () => string;
}

export const createBrandModule = ({
    currentEnvironment,
    getDeviceId,
}: CreateBrandModuleProps) => {
    const LINK_ID = 'dazn-themepark-link';
    const sessionStorageKey = 'brand';
    const isProd = currentEnvironment === environments.PRODUCTION;

    const brandTitleMap: Record<boot.brand.BrandName, string> = {
        dazn: 'DAZN',
        kayo: 'Kayo Sports',
        binge: 'Binge',
        foxtelgnc: 'Foxtel',
        foxsports: 'Fox Sports',
    };

    const getBrandNameFromUrl = () => {
        // Check for SSR-provided brand first
        if (
            (window as any).daznSSR?.brand &&
            boot.brand.allowedBrandNames.includes(
                (window as any).daznSSR.brand as boot.brand.BrandName,
            )
        ) {
            return (window as any).daznSSR.brand as boot.brand.BrandName;
        }

        //check for query params - Hack till the time kayo domains are not ready
        const params = new URLSearchParams(window.location.search);
        const brandFromQuery = params.get('brand');
        if (
            brandFromQuery &&
            boot.brand.allowedBrandNames.includes(
                brandFromQuery.toLowerCase() as boot.brand.BrandName,
            )
        ) {
            return brandFromQuery.toLowerCase() as boot.brand.BrandName;
        }

        //Actual logic - if no query params, check the host
        const urlHost = window.location.host;
        if (urlHost.includes('kayo')) return 'kayo';
        if (urlHost.includes('binge')) return 'binge';
        if (urlHost.includes('foxtel')) return 'foxtelgnc';
        if (urlHost.includes('foxsports')) return 'foxsports';
        return 'dazn';
    };

    function waitForLoad(link: HTMLLinkElement): Promise<void> {
        return new Promise((resolve, reject) => {
            if (link.sheet) return resolve();
            link.onload = () => resolve();
            link.onerror = () => reject(new Error('Failed to load theme CSS'));
        });
    }

    function fetchManifest(): Promise<Record<string, any>> {
        const manifestUrl = isProd
            ? 'https://themepark.fe.indazn.com/manifest.json'
            : 'https://themepark.fe.dazn-stage.com/manifest.json';
        return fetch(manifestUrl).then(res => {
            if (!res.ok) {
                throw new Error(`Failed to fetch manifest: ${res.statusText}`);
            }
            return res.json();
        });
    }

    function exchangeToken(requestBody: any): Promise<any> {
        const authUrl = isProd
            ? 'https://authentication-prod.ar.indazn.com/v1/authenticate'
            : 'https://authentication-stage.ar.dazn-stage.com/v1/authenticate';

        return fetch(authUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        }).then(res => {
            if (!res.ok) {
                window.newrelic?.addPageAction('FoxtelTokenMigration', {
                    step: 'APIError',
                    ApiResponse: JSON.stringify(res),
                });
                throw new Error(`Failed to exchange token: ${res.statusText}`);
            }
            return res.json();
        });
    }

    function getPartnerData(field: string): string | null {
        const sharedDeviceInfo = localStorage.getItem('SHARED_DEVICE');
        if (!sharedDeviceInfo) return null;

        try {
            const parsedInfo = JSON.parse(sharedDeviceInfo);
            return parsedInfo?.[field] || null;
        } catch (err) {
            console.error(
                'Failed to parse MISL.lastUser from localStorage',
                err,
            );
            return null;
        }
    }

    async function runBrandMigrationForUserToken(brandName: string) {
        try {
            // Check if migration already completed for this device
            const migrationKey = 'foxtelTokenMigrationCompleted';
            if (localStorage.getItem(migrationKey)) {
                return; // Already migrated on this device
            }

            if (
                brandName?.toLowerCase() !== 'foxtelgnc' ||
                localStorage.getItem('MISL.authToken')
            )
                return;

            // 1. Find Foxtel token key from browser storage
            const partnerToken = getPartnerData('loginToken');
            const partnerDeviceId = getPartnerData('deviceId-v3');
            const partnerLastUser = getPartnerData('lastUser');

            if (!partnerToken || !partnerDeviceId || !partnerLastUser) return;

            // 3. Prepare request body
            const requestBody = {
                idpType: 'idp-partner-token',
                idpPayload: {
                    brand: brandName?.toLowerCase(),
                    type: brandName?.toLowerCase(),
                    partnerToken,
                    deviceId: partnerDeviceId,
                    identifier: partnerLastUser,
                },
                deviceKey: getDeviceId(),
                idpMetadata: {
                    version: 1,
                    partnerName: 'dazn',
                },
            };

            // 4. Make API call
            window.newrelic?.addPageAction('FoxtelTokenMigration', {
                step: 'triggeringAPICall',
                payload: JSON.stringify(requestBody),
            });

            const responseData = await exchangeToken(requestBody);

            const token = responseData?.accessToken;
            if (!token) {
                window.newrelic?.addPageAction('FoxtelTokenMigration', {
                    step: 'TokenMissingInResponse',
                    responseData: JSON.stringify(responseData),
                });
                return;
            }

            // 6. Store jwtToken locally
            localStorage.setItem('MISL.authToken', token);

            // Mark migration as completed for this device
            localStorage.setItem(migrationKey, 'true');

            window.newrelic?.addPageAction('FoxtelTokenMigration', {
                step: 'Completed',
                daznId: responseData?.identity?.daznId || '',
            });
        } catch (err) {
            window.newrelic?.addPageAction('FoxtelTokenMigration', {
                step: 'GenericError',
                errorMessage: JSON.stringify(err),
            });
        }
    }

    function updateBrand(brandName: string): Promise<void> {
        if (typeof window === 'undefined' || !document)
            return Promise.resolve();

        if (
            !boot.brand.allowedBrandNames.includes(
                brandName as boot.brand.BrandName,
            )
        ) {
            throw new Error(
                `Invalid brand name '${brandName}'. Allowed values: ${boot.brand.allowedBrandNames.join(
                    ', ',
                )}`,
            );
        }
        window.document.title =
            brandTitleMap[brandName as boot.brand.BrandName];

        return fetchManifest().then(manifest => {
            const cssUrl = manifest.files?.[brandName];
            if (!cssUrl) {
                throw new Error(
                    `Theme CSS URL not found in manifest for ${brandName}`,
                );
            }

            document.documentElement.setAttribute('data-theme', brandName);

            const previousLink = document.getElementById(
                LINK_ID,
            ) as HTMLLinkElement | null;
            previousLink?.remove();

            const link = document.createElement('link');
            link.className = 'protected';
            link.id = LINK_ID;
            link.rel = 'stylesheet';
            link.href = cssUrl;
            document.head.appendChild(link);
            return waitForLoad(link).then(() => {
                window.newrelic?.setCustomAttribute('brand', brandName);
                sessionStorage.setItem(sessionStorageKey, brandName);
            });
        });
    }

    return {
        run: async () => {
            const params = new URLSearchParams(window.location.search);
            const brandFromQuery = params.get('brand');
            const brandName =
                brandFromQuery ??
                (sessionStorage.getItem(
                    sessionStorageKey,
                ) as boot.brand.BrandName) ??
                getBrandNameFromUrl();

            await runBrandMigrationForUserToken(brandName);
            return updateBrand(brandName);
        },

        getBrandName: (): boot.brand.BrandName =>
            (sessionStorage.getItem(
                sessionStorageKey,
            ) as boot.brand.BrandName) ?? getBrandNameFromUrl(),

        updateBrand: (brandName: boot.brand.BrandName) =>
            updateBrand(brandName),
    };
};
